import {
    useCallback,
    useEffect,
    useState
} from 'react';

import {
    createComment,
    deleteComment,
    getComments
} from '../api/commentApi';

import {
    getShowcaseComments,
    createShowcaseComment
} from '../api/photoApi';

import { openCustomAlert } from './common/useCustomAlert';
import ReportButton from './ReportButton';
import './CommentSection.css';

interface Props {
    postId: number;
    type?: 'post' | 'showcase';
    onCommentAdded?: () => void;
    initialContent?: string;
}

const CommentSection = (
    { postId , type = 'post', onCommentAdded, initialContent = ''}: Props
) => {

    // 댓글 상태
    const [comments, setComments] =
        useState<any[]>([]);

    // 입력 상태
    const [content, setContent] =
        useState(initialContent);

    useEffect(() => {
        if (initialContent) setContent(initialContent);
    }, [initialContent, postId]);

    // 댓글 조회
    const loadComments = useCallback(async () => {
        try {
            const data = type === 'post'
                ? await getComments(postId)
                : await getShowcaseComments(postId);

            setComments(data);
        } catch (e) {
            console.error(e);
        }
    }, [postId, type]);

    // 최초 실행
    useEffect(() => {
        loadComments();
    }, [loadComments]);

    // 댓글 작성
    const handleCreateComment = async () => {
        try {
            const token = localStorage.getItem('token');

            if(!token) {
                alert('로그인이 필요합니다');
                return;
            }

            if(!content.trim()) {
                return;
            }

            if (type === 'post') {
                await createComment(postId, content);
            } else {
                await createShowcaseComment(postId, content);
            }

            setContent('');
            await loadComments();
            if(onCommentAdded) {
                onCommentAdded();
            }
        } catch (e) {
            console.error(e);
            alert('댓글 등록 실패');
        }
    };

    // 댓글 삭제
    const handleDeleteComment = (
        commentId: number
    ) => {
        openCustomAlert({
            message: '댓글을 삭제하시겠습니까?',
            type: 'confirm',
            onConfirm: async () => {
                try {
                    await deleteComment(commentId);

                    setComments(
                        comments.filter(
                            (comment) =>
                                comment.id !== commentId
                        )
                    );

                    openCustomAlert({
                        message: '댓글이 삭제되었습니다.',
                        type: 'success'
                    });

                    if(onCommentAdded) {
                        onCommentAdded();
                    }
                } catch (e) {
                    console.error(e);
                    openCustomAlert({
                        message: '댓글 삭제 실패',
                        type: 'error'
                    });
                }
            }
        });
    };

    return (
        <div className="comment-section">
            {/* 댓글 목록 */}
            <div className="comment-list">
                {
                    comments.map((comment: any) => (
                        <div
                            className="comment-card"
                            key={comment.id}
                        >
                            {/* 내용 */}
                            <div className="comment-content">
                                {comment.content}
                            </div>

                            {/* 하단 */}
                            <div className="comment-bottom-row">
                                {/* 사용자 */}
                                <div className="comment-user-wrap">
                                    {/* 아바타 */}
                                    <div className="comment-avatar">
                                        {
                                            comment.nickname
                                                ?.charAt(0)
                                                ?.toUpperCase()
                                        }
                                    </div>

                                    {/* 닉네임 + 날짜 */}
                                    <div className="comment-inline-meta">
                                        <span className="comment-nickname">
                                            {comment.nickname}
                                        </span>
                                        <span className="comment-dot">
                                            ·
                                        </span>
                                        <span className="comment-date">
                                            {
                                                new Date(
                                                    comment.createdAt
                                                ).toLocaleString(
                                                    'sv-SE'
                                                ).replace(
                                                    ',',
                                                    ''
                                                )
                                            }
                                        </span>
                                    </div>
                                </div>

                                {/* 삭제 */}
                                {type === 'post' && <ReportButton targetType="COMMENT" targetId={comment.id} />}
                                {
                                    String(comment.userId) === String(localStorage.getItem('userId'))
                                    && (
                                        <button
                                            type="button"
                                            className="comment-item-delete-btn"
                                            aria-label={`${comment.nickname || '사용자'} 댓글 삭제`}
                                            onClick={() =>
                                                handleDeleteComment(
                                                    comment.id
                                                )
                                            }
                                        >
                                            삭제
                                        </button>
                                    )
                                }
                            </div>
                        </div>
                    ))
                }
            </div>

            {/* 작성 카드 (하단으로 이동) */}
            <div className="comment-write-card">
                <textarea
                    aria-label="댓글"
                    placeholder="당신의 생각을 남겨보세요"
                    value={content}
                    onChange={(e) =>
                        setContent(
                            e.target.value
                        )
                    }
                    className="comment-textarea"
                />

                <div className="comment-submit-wrap">
                    <button
                        type="button"
                        className="comment-submit-button"
                        onClick={handleCreateComment}
                    >
                        작성
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CommentSection;
